/**
 * ConversationsSection.jsx
 * 
 * Displays conversation history for a specific patient in the PatientDetail page.
 * Features pagination, transcript/summary toggle, and PDF export.
 * 
 * Usage:
 *   <ConversationsSection patientId="PT001" patientName="John Doe" />
 */

import React, { useState, useEffect, useCallback } from 'react'
import { 
  MessageSquare, 
  Clock, 
  Download, 
  ChevronDown, 
  ChevronUp,
  AlertTriangle,
  FileText,
  RefreshCw,
  Eye,
  EyeOff
} from 'lucide-react'
import { mdApi } from '../api/mdApi'
import { jsPDF } from 'jspdf'
import './ConversationsSection.css'

// ============================================================================
// Constants
// ============================================================================

/** Number of conversations to load per page */
const PAGE_SIZE = 10

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format a date string to a relative time or formatted date
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted time string
 */
const formatConversationTime = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  
  if (diffHours < 1) {
    const diffMins = Math.floor(diffMs / (1000 * 60))
    return diffMins < 1 ? 'Just now' : `${diffMins} min ago`
  } else if (diffHours < 24) {
    return `${diffHours} hours ago`
  } else {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }
}

/**
 * Format duration in minutes to readable string
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration
 */
const formatDuration = (minutes) => {
  if (!minutes) return 'Unknown'
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
}

/**
 * Format a tag by replacing underscores with spaces and capitalizing each word
 * Example: "chest_pain" -> "Chest Pain"
 * @param {string} tag - The tag to format
 * @returns {string} Formatted tag
 */
const formatTag = (tag) => {
  if (!tag || typeof tag !== 'string') return ''
  return tag
    .replace(/_/g, ' ')  // Replace underscores with spaces
    .replace(/\b\w/g, (char) => char.toUpperCase())  // Capitalize first letter of each word
}

/**
 * Parse transcript data which may be a string (JSON) or array
 * @param {string|Array} transcript - Transcript data
 * @returns {Array} Parsed transcript array
 */
const parseTranscript = (transcript) => {
  if (!transcript) return []
  
  // If already an array, return it
  if (Array.isArray(transcript)) return transcript
  
  // If it's a string, try to parse it as JSON
  if (typeof transcript === 'string') {
    try {
      const parsed = JSON.parse(transcript)
      return Array.isArray(parsed) ? parsed : []
    } catch (e) {
      // If JSON parsing fails, it might be a plain text transcript
      // Return it as a single message
      return [{
        role: 'system',
        content: transcript,
        timestamp: null
      }]
    }
  }
  
  return []
}

// ============================================================================
// PDF Generation
// ============================================================================

/**
 * Generate a PDF transcript of a conversation
 * @param {Object} conversation - Conversation data
 * @param {string} patientName - Patient full name
 * @param {Object} patientInfo - Additional patient info (DOB, MRN)
 */
const generateTranscriptPDF = (conversation, patientName, patientInfo = {}) => {
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 20
  const lineHeight = 7
  let yPos = margin
  
  // Helper to add text with word wrap
  const addWrappedText = (text, x, y, maxWidth, fontSize = 10) => {
    doc.setFontSize(fontSize)
    const lines = doc.splitTextToSize(text, maxWidth)
    lines.forEach((line, i) => {
      if (y + (i * lineHeight) > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage()
        y = margin
      }
      doc.text(line, x, y + (i * lineHeight))
    })
    return y + (lines.length * lineHeight)
  }
  
  // Title
  doc.setFontSize(16)
  doc.setFont('helvetica', 'bold')
  doc.text('CONVERSATION TRANSCRIPT', pageWidth / 2, yPos, { align: 'center' })
  yPos += 8
  
  doc.setFontSize(11)
  doc.setFont('helvetica', 'normal')
  doc.text('Lucena Health Medical Assistant', pageWidth / 2, yPos, { align: 'center' })
  yPos += 15
  
  // Separator line
  doc.setDrawColor(200)
  doc.line(margin, yPos, pageWidth - margin, yPos)
  yPos += 10
  
  // Patient Information
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  doc.text(`Patient: ${patientName}`, margin, yPos)
  yPos += lineHeight
  
  doc.setFont('helvetica', 'normal')
  if (patientInfo.dob) {
    doc.text(`DOB: ${patientInfo.dob}`, margin, yPos)
    if (patientInfo.mrn) {
      doc.text(`MRN: ${patientInfo.mrn}`, margin + 60, yPos)
    }
    yPos += lineHeight
  }
  
  const convDate = new Date(conversation.created_at || conversation.date)
  doc.text(`Date: ${convDate.toLocaleDateString('en-US', { 
    weekday: 'long',
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })}`, margin, yPos)
  yPos += lineHeight
  
  if (conversation.duration) {
    doc.text(`Duration: ${formatDuration(conversation.duration)}`, margin, yPos)
    yPos += lineHeight
  }
  
  if (conversation.channel) {
    doc.text(`Channel: ${conversation.channel}`, margin, yPos)
    yPos += lineHeight
  }
  
  yPos += 5
  
  // AI Summary Section
  if (conversation.summary) {
    doc.setDrawColor(100)
    doc.line(margin, yPos, pageWidth - margin, yPos)
    yPos += 8
    
    doc.setFont('helvetica', 'bold')
    doc.text('AI SUMMARY', margin, yPos)
    yPos += lineHeight
    
    doc.setFont('helvetica', 'normal')
    yPos = addWrappedText(conversation.summary, margin, yPos, pageWidth - (margin * 2))
    yPos += 5
  }
  
  // Concerns Section
  if (conversation.concerns && conversation.concerns.length > 0) {
    doc.setDrawColor(100)
    doc.line(margin, yPos, pageWidth - margin, yPos)
    yPos += 8
    
    doc.setFont('helvetica', 'bold')
    doc.text('CONCERNS FLAGGED', margin, yPos)
    yPos += lineHeight
    
    doc.setFont('helvetica', 'normal')
    conversation.concerns.forEach(concern => {
      const severityIcon = concern.severity === 'urgent' || concern.severity === 'high' ? '⚠️' : '⚡'
      const concernText = `${severityIcon} ${concern.severity?.toUpperCase()}: ${concern.description}`
      yPos = addWrappedText(concernText, margin + 5, yPos, pageWidth - (margin * 2) - 5)
      
      if (concern.status) {
        doc.setFontSize(9)
        doc.text(`   Status: ${concern.status}`, margin + 5, yPos)
        yPos += lineHeight
        doc.setFontSize(10)
      }
      yPos += 3
    })
    yPos += 2
  }
  
  // Full Transcript Section
  const parsedTranscript = parseTranscript(conversation.transcript)
  if (parsedTranscript && parsedTranscript.length > 0) {
    doc.setDrawColor(100)
    doc.line(margin, yPos, pageWidth - margin, yPos)
    yPos += 8
    
    doc.setFont('helvetica', 'bold')
    doc.text('FULL TRANSCRIPT', margin, yPos)
    yPos += lineHeight + 3
    
    parsedTranscript.forEach(message => {
      // Speaker label
      doc.setFont('helvetica', 'bold')
      const speaker = message.role === 'assistant' ? 'LUNA' : 'PATIENT'
      const timestamp = message.timestamp ? `[${message.timestamp}] ` : ''
      doc.text(`${timestamp}${speaker}:`, margin, yPos)
      yPos += lineHeight
      
      // Message content
      doc.setFont('helvetica', 'normal')
      yPos = addWrappedText(message.content, margin + 5, yPos, pageWidth - (margin * 2) - 5)
      yPos += 5
    })
  }
  
  // Footer
  doc.setFontSize(8)
  doc.setTextColor(128)
  const pageCount = doc.internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.text(
      `Page ${i} of ${pageCount} | Generated: ${new Date().toLocaleString()}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    )
  }
  
  // Save the PDF
  const fileName = `transcript_${patientName.replace(/\s+/g, '_')}_${convDate.toISOString().split('T')[0]}.pdf`
  doc.save(fileName)
}

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * TranscriptView - Displays transcript with proper parsing
 */
const TranscriptView = ({ transcript }) => {
  const parsedTranscript = parseTranscript(transcript)
  
  return (
    <div className="conv-transcript">
      <h4>Transcript</h4>
      {parsedTranscript && parsedTranscript.length > 0 ? (
        <div className="transcript-messages">
          {parsedTranscript.map((message, idx) => (
            <div
              key={idx}
              className={`transcript-message ${message.role === 'assistant' ? 'assistant' : 'patient'}`}
            >
              <span className="message-role">
                {message.role === 'assistant' ? 'LUNA' : 'PATIENT'}
              </span>
              {message.timestamp && (
                <span className="message-time">{message.timestamp}</span>
              )}
              <p className="message-content">{message.content}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="no-transcript">Full transcript not available.</p>
      )}
    </div>
  )
}

/**
 * ConversationCard - Individual conversation display with expand/collapse
 */
const ConversationCard = ({ 
  conversation, 
  patientName, 
  patientInfo,
  isExpanded, 
  onToggleExpand, 
  showSummary,
  onToggleView 
}) => {
  const [exporting, setExporting] = useState(false)
  
  const handleExportPDF = async (e) => {
    e.stopPropagation()
    setExporting(true)
    try {
      generateTranscriptPDF(conversation, patientName, patientInfo)
    } catch (err) {
      console.error('Error generating PDF:', err)
    } finally {
      setExporting(false)
    }
  }
  
  const timeFormatted = formatConversationTime(conversation.created_at || conversation.date)
  // Format topics by replacing underscores and capitalizing each word
  const topics = conversation.topics_discussed?.map(formatTag).join(', ') ||
                 conversation.summary?.slice(0, 50) ||
                 'General conversation'
  
  return (
    <div className={`conversation-card ${isExpanded ? 'expanded' : ''}`}>
      {/* Card Header - Always visible */}
      <div className="conversation-card-header" onClick={() => onToggleExpand(conversation.id)}>
        <div className="conv-header-left">
          <MessageSquare size={18} className="conv-icon" />
          <div className="conv-header-info">
            <div className="conv-date">
              {new Date(conversation.created_at || conversation.date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </div>
            <div className="conv-topic">{topics}</div>
          </div>
        </div>
        
        <div className="conv-header-right">
          {conversation.requires_clinical_review && (
            <span className="review-badge" title="Requires clinical review">
              <AlertTriangle size={14} />
              Needs Review
            </span>
          )}
          
          <span className="conv-time">
            <Clock size={14} />
            {timeFormatted}
          </span>
          
          <button 
            className="btn-export-pdf"
            onClick={handleExportPDF}
            disabled={exporting}
            title="Export as PDF"
            aria-label="Export conversation as PDF"
          >
            <Download size={14} />
            {exporting ? 'Exporting...' : 'PDF'}
          </button>
          
          <span className="expand-indicator">
            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </span>
        </div>
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="conversation-card-content">
          {/* View Toggle */}
          <div className="view-toggle">
            <button 
              className={`toggle-btn ${showSummary ? 'active' : ''}`}
              onClick={() => onToggleView('summary')}
            >
              <FileText size={14} />
              Summary
            </button>
            <button 
              className={`toggle-btn ${!showSummary ? 'active' : ''}`}
              onClick={() => onToggleView('transcript')}
            >
              <MessageSquare size={14} />
              Full Transcript
            </button>
          </div>
          
          {/* Metadata */}
          <div className="conv-metadata">
            {conversation.duration && (
              <span>Duration: {formatDuration(conversation.duration)}</span>
            )}
            {conversation.channel && (
              <span>Channel: {conversation.channel}</span>
            )}
            {conversation.has_concerning_issues && (
              <span className="has-concerns">Contains flagged concerns</span>
            )}
          </div>
          
          {/* Content Area */}
          {showSummary ? (
            <div className="conv-summary">
              <h4>AI Summary</h4>
              <p>{conversation.summary || 'No summary available for this conversation.'}</p>
              
              {/* Show concerns if any */}
              {conversation.concerns && conversation.concerns.length > 0 && (
                <div className="conv-concerns">
                  <h4>Flagged Concerns</h4>
                  <ul>
                    {conversation.concerns.map((concern, idx) => (
                      <li key={idx} className={`concern-${concern.severity}`}>
                        <span className="concern-severity">{concern.severity?.toUpperCase()}</span>
                        <span className="concern-desc">{concern.description}</span>
                        {concern.status && (
                          <span className="concern-status">{concern.status}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <TranscriptView transcript={conversation.transcript} />
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

function ConversationsSection({ patientId, patientName, patientInfo = {} }) {
  // State
  const [conversations, setConversations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  
  // UI State
  const [expandedId, setExpandedId] = useState(null)
  const [viewModes, setViewModes] = useState({}) // { [convId]: 'summary' | 'transcript' }
  const [filterReviewNeeded, setFilterReviewNeeded] = useState(true) // Default to showing only items needing review
  
  // ============================================================================
  // Data Fetching
  // ============================================================================
  
  /**
   * Load conversations for the patient
   */
  const loadConversations = useCallback(async (append = false) => {
    try {
      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }
      setError(null)
      
      const currentCount = append ? conversations.length : 0
      const data = await mdApi.getPatientConversations(patientId, PAGE_SIZE + currentCount)
      
      // Check if we have more data to load
      const newConversations = append ? data.slice(currentCount) : data
      setHasMore(newConversations.length >= PAGE_SIZE)
      
      if (append) {
        setConversations(prev => [...prev, ...newConversations])
      } else {
        setConversations(data || [])
      }
    } catch (err) {
      console.error('Error loading conversations:', err)
      setError('Failed to load conversations. Please try again.')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [patientId, conversations.length])
  
  // Initial load
  useEffect(() => {
    loadConversations()
  }, [patientId]) // eslint-disable-line react-hooks/exhaustive-deps
  
  // ============================================================================
  // Event Handlers
  // ============================================================================
  
  const handleToggleExpand = (convId) => {
    setExpandedId(prev => prev === convId ? null : convId)
    // Default to summary view when expanding
    if (!viewModes[convId]) {
      setViewModes(prev => ({ ...prev, [convId]: 'summary' }))
    }
  }
  
  const handleToggleView = (convId, view) => {
    setViewModes(prev => ({ ...prev, [convId]: view }))
  }
  
  const handleLoadMore = () => {
    loadConversations(true)
  }
  
  const handleRefresh = () => {
    loadConversations(false)
  }
  
  // ============================================================================
  // Filtered Data
  // ============================================================================
  
  const filteredConversations = filterReviewNeeded
    ? conversations.filter(c => c.requires_clinical_review)
    : conversations
  
  // ============================================================================
  // Render
  // ============================================================================
  
  if (loading) {
    return (
      <div className="conversations-section loading">
        <div className="spinner"></div>
        <p>Loading conversations...</p>
      </div>
    )
  }
  
  return (
    <div className="conversations-section">
      {/* Section Header */}
      <div className="section-header">
        <h3>
          <MessageSquare size={20} />
          Conversation History
        </h3>
        
        <div className="section-controls">
          <label className="filter-toggle">
            <input
              type="checkbox"
              checked={filterReviewNeeded}
              onChange={(e) => setFilterReviewNeeded(e.target.checked)}
            />
            <span>Show only needs review</span>
          </label>
          
          <button 
            className="btn-refresh-conversations"
            onClick={handleRefresh}
            disabled={loading}
            aria-label="Refresh conversations"
          >
            <RefreshCw size={14} className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>
      
      {/* Error State */}
      {error && (
        <div className="conversations-error">
          <AlertTriangle size={20} />
          <span>{error}</span>
          <button onClick={handleRefresh}>Retry</button>
        </div>
      )}
      
      {/* Conversations List */}
      {filteredConversations.length === 0 ? (
        <div className="no-conversations">
          <MessageSquare size={32} />
          <p>
            {filterReviewNeeded 
              ? 'No conversations requiring review.'
              : 'No conversations recorded yet.'
            }
          </p>
        </div>
      ) : (
        <div className="conversations-list">
          {filteredConversations.map(conversation => (
            <ConversationCard
              key={conversation.id}
              conversation={conversation}
              patientName={patientName}
              patientInfo={patientInfo}
              isExpanded={expandedId === conversation.id}
              onToggleExpand={handleToggleExpand}
              showSummary={viewModes[conversation.id] !== 'transcript'}
              onToggleView={(view) => handleToggleView(conversation.id, view)}
            />
          ))}
        </div>
      )}
      
      {/* Load More Button */}
      {hasMore && filteredConversations.length > 0 && !filterReviewNeeded && (
        <button 
          className="btn-load-more"
          onClick={handleLoadMore}
          disabled={loadingMore}
        >
          {loadingMore ? 'Loading...' : 'Load More Conversations'}
        </button>
      )}
    </div>
  )
}

export default ConversationsSection